import { useState, useCallback, useMemo } from 'react';
import { useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useGetProspectById } from '../../../../api/services/lupp/prospect/prospect';
import { useLocaleNavigate } from '../../../../hooks/useLocaleNavigate';
import type { CheckoutParams } from '../../../../components/Configurator/Configurator';

/**
 * Custom hook for managing prospect URL state (selection, views, tabs, checkout params)
 * @returns URL state and navigation handlers
 */
export const useProspectUrlState = () => {
  const navigate = useLocaleNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { prospectId: selectedProspectId, view } = useParams<{
    prospectId?: string;
    view?: string;
  }>();

  // Capture prospect ID from URL on initial mount (refresh scenario)
  const [initialProspectId] = useState(selectedProspectId);

  // Derive views from URL
  const showConfigurator = view === 'inventory' || view === 'checkout';
  const showCheckout = view === 'checkout';

  // Get active tab from URL hash (e.g., #details, #quotations)
  const activeTab = location.hash ? location.hash.slice(1) : 'details';

  // Parse checkout params from URL search params
  const checkoutParams = useMemo<CheckoutParams | null>(() => {
    if (!showCheckout) return null;
    const batchNumber = searchParams.get('batchNumber');
    const uuid = searchParams.get('uuid');
    const type = searchParams.get('type') as 'new' | 'demo' | 'pre-owned' | null;
    if (batchNumber && uuid && type) {
      return { batchNumber, uuid, type };
    }
    return null;
  }, [showCheckout, searchParams]);

  // Parse shortcode from URL (for inventory view with shortcode)
  const shortcodeFromUrl = showConfigurator ? searchParams.get('shortcode') : null;

  // Parse quoteId from URL (for loading checkout from existing quote)
  const quoteIdFromUrl = showCheckout ? searchParams.get('quoteId') : null;

  // Fetch selected prospect data when prospect is selected
  const hasInitialProspectInUrl = !!initialProspectId && selectedProspectId === initialProspectId;
  const { data: selectedProspectData } = useGetProspectById(
    selectedProspectId || '',
    !!selectedProspectId
  );

  // Navigation handlers
  const handleProspectSelect = useCallback(
    (prospectId: string | null) => {
      if (prospectId) {
        const hash = location.hash || '#details';
        navigate(`/prospect-desk/prospects/${prospectId}${hash}`);
      } else {
        navigate('/prospect-desk/prospects');
      }
    },
    [navigate, location.hash]
  );

  const handleTabChange = useCallback(
    (tabId: string) => {
      navigate(`${location.pathname}${location.search}#${tabId}`, { replace: true });
    },
    [navigate, location.pathname, location.search]
  );

  const handleCreateQuotation = useCallback(
    (prospectId?: string) => {
      const id = prospectId || selectedProspectId;
      if (id) {
        navigate(`/prospect-desk/prospects/${id}/inventory`);
      }
    },
    [navigate, selectedProspectId]
  );

  const handleCreateProspect = useCallback(() => {
    navigate('/prospect-desk/create-new-prospect');
  }, [navigate]);

  const handleBackToProspects = useCallback(() => {
    if (selectedProspectId) {
      navigate(`/prospect-desk/prospects/${selectedProspectId}#quotations`);
    } else {
      navigate('/prospect-desk/prospects');
    }
  }, [navigate, selectedProspectId]);

  const handleCheckoutNavigate = useCallback(
    (params: CheckoutParams) => {
      const searchParamsString = new URLSearchParams({
        batchNumber: params.batchNumber,
        uuid: params.uuid,
        type: params.type,
      }).toString();
      navigate(`/prospect-desk/prospects/${selectedProspectId}/checkout?${searchParamsString}`);
    },
    [navigate, selectedProspectId]
  );

  const handleCheckoutBack = useCallback(() => {
    navigate(`/prospect-desk/prospects/${selectedProspectId}/inventory`);
  }, [navigate, selectedProspectId]);

  const handleShortcodeNavigate = useCallback(
    (shortCode: string) => {
      navigate(`/prospect-desk/prospects/${selectedProspectId}/inventory?shortcode=${shortCode}`);
    },
    [navigate, selectedProspectId]
  );

  const handleQuoteClick = useCallback(
    (quoteId: string) => {
      navigate(`/prospect-desk/prospects/${selectedProspectId}/checkout?quoteId=${quoteId}`);
    },
    [navigate, selectedProspectId]
  );

  const handleBackToProspectDetails = useCallback(
    (tabId: string) => {
      navigate(`/prospect-desk/prospects/${selectedProspectId}#${tabId}`);
    },
    [navigate, selectedProspectId]
  );

  return {
    // URL state
    selectedProspectId,
    activeTab,
    showConfigurator,
    showCheckout,
    checkoutParams,
    quoteIdFromUrl,
    shortcodeFromUrl,

    // Single prospect view data (when loaded from URL)
    hasInitialProspectInUrl,
    selectedProspectData,

    // Navigation handlers
    onProspectSelect: handleProspectSelect,
    onTabChange: handleTabChange,
    onCreateQuotation: handleCreateQuotation,
    onCreateProspect: handleCreateProspect,
    onBackToProspects: handleBackToProspects,
    onCheckoutNavigate: handleCheckoutNavigate,
    onShortcodeNavigate: handleShortcodeNavigate,
    onCheckoutBack: handleCheckoutBack,
    onQuoteClick: handleQuoteClick,
    onBackToProspectDetails: handleBackToProspectDetails,
  };
};
